import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { CallGroup, CliSignalledNumber, Language, PhoneServiceDetail, Skill } from './phone-service-api.model';
import { environment } from '../../../environments/environment';
@Injectable({ providedIn: 'root' })
export class PhoneServiceApiService {
  private readonly http = inject(HttpClient);

  getById(psId: number): Observable<PhoneServiceDetail> {
    return this.http.get<PhoneServiceDetail>(`${environment.phoneServiceUrl}/phoneservices/${psId}`)
      .pipe(map(ps => ({ ...ps, id: Number(ps.id) })));
  }

  /**
   * List all phone services attached to a given contract — used to detect
   * shared contracts (length > 1) before allowing a single-account end-contract
   * action. Returns raw shape; callers typically only need `.length`.
   */
  listByContractId(contractId: number | string): Observable<unknown[]> {
    return this.http.get<unknown[]>(`${environment.phoneServiceUrl}/phoneservices`, {
      params: { contractId: String(contractId) },
    });
  }

  save(ps: PhoneServiceDetail): Observable<PhoneServiceDetail> {
    return this.http.put<PhoneServiceDetail>(`${environment.phoneServiceUrl}/phoneservices`, ps);
  }

  getSignalledNumber(psId: number): Observable<CliSignalledNumber> {
    return this.http.get<CliSignalledNumber>(`${environment.phoneServiceUrl}/cli-verification/number/signalled`, {
      params: { phoneServiceId: String(psId) },
    });
  }

  getLanguages(brandId: number): Observable<Language[]> {
    return this.http.get<Language[]>(`${environment.phoneServiceUrl}/languages`, {
      params: { brandId: brandId.toString() },
    });
  }

  /** All voicebox/answering-machine languages (NOT brand-filtered) — old CMT's full AM list. */
  getAllLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${environment.phoneServiceUrl}/languages`);
  }

  getVoicebotModes(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.phoneServiceUrl}/enums/voicebot/modes`);
  }

  getCallGroups(): Observable<CallGroup[]> {
    return this.http.get<CallGroup[]>(`${environment.phoneServiceUrl}/routings/callgroups`);
  }

  // Skills
  getSkills(psId: number): Observable<Skill[]> {
    return this.http.get<Skill[]>(`${environment.phoneServiceUrl}/phoneservices/${psId}/skills`);
  }

  getAddableSkills(psId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.phoneServiceUrl}/phoneservices/${psId}/skills/addable`);
  }

  addSkill(psId: number, skill: any, employeeId: string): Observable<any> {
    return this.http.post(`${environment.phoneServiceUrl}/phoneservices/${psId}/skills`, skill, {
      params: { employeeId },
    });
  }

  updateSkill(psId: number, skill: any, employeeId: string): Observable<any> {
    return this.http.put(`${environment.phoneServiceUrl}/phoneservices/${psId}/skills`, skill, {
      params: { employeeId },
    });
  }

  removeSkill(psId: number, skillName: string, employeeId: string): Observable<void> {
    return this.http.delete<void>(`${environment.phoneServiceUrl}/phoneservices/${psId}/skills/${skillName}`, {
      params: { employeeId },
    });
  }

  getCaiLink(psId: number, employeeId: string): Observable<{ linkToCai?: string }> {
    return this.http.get<{ linkToCai?: string }>(`${environment.phoneServiceUrl}/phoneservices/${psId}`, {
      params: { employeeId },
    });
  }
}
